import { usePermissions } from '@shared/hooks/usePermissions';
import { useCallback } from 'react';
import { DropdownItem, MenuItem } from './types';
import { useCRMEnabled } from '@modules/CRM/shared/hooks/useCRMSettings';
import { useProfitabilityAccess } from '@modules/Reports/Profitability/hooks/useProfitabilityAccess';

/**
 * Hook que filtra items del menú basado en teamTypes, permissions, requireRoleAdmin, requireWalletVisible y requireCRMEnabled del usuario
 */
export function useMenuPermissions() {
  const { canAccess, hasAllPermissions, hasAnyPermission, isAdminRole, isWalletVisible, isHoldedAccounting } =
    usePermissions();
  const { crmEnabled } = useCRMEnabled();
  const profitabilityAccess = useProfitabilityAccess();

  /**
   * Filtra items del menú recursivamente
   * - Verifica teamTypes si están especificados
   * - Verifica permissions si están especificados
   * - Si requireAll es true, requiere TODOS los permisos
   * - Si requireAll es false o no está definido, requiere AL MENOS UNO
   */
  const filterMenuItems = useCallback(
    (items: MenuItem[]): MenuItem[] => {
      return items
        .map((item) => {
          // Si el item tiene children, filtrarlos recursivamente
          let filteredChildren: MenuItem[] | undefined;
          if (item.children && item.children.length > 0) {
            filteredChildren = filterMenuItems(item.children);
          }

          // Verificar acceso al item
          let hasAccess = true;

          // Si tiene teamTypes O permissions definidos, verificar acceso
          if (item.teamTypes || item.permissions) {
            if (item.requireAll && item.permissions && item.permissions.length > 0) {
              // Verificar team_type Y todos los permisos
              hasAccess = canAccess(item.teamTypes, []) && hasAllPermissions(item.permissions);
            } else {
              // Verificación estándar: team_type + al menos uno de los permisos
              hasAccess = canAccess(item.teamTypes, item.permissions);
            }
          }

          // Si no tiene acceso, no incluir el item
          if (!hasAccess) {
            return null;
          }

          // Verificar requireRoleAdmin (si está definido)
          if (item.requireRoleAdmin && !isAdminRole()) {
            return null;
          }

          // Verificar requireWalletVisible (si está definido)
          if (item.requireWalletVisible && !isWalletVisible()) {
            return null;
          }

          // Verificar requireCRMEnabled (si está definido)
          if (item.requireCRMEnabled && !crmEnabled) {
            return null;
          }

          if (item.requireProfitabilityAccess && !profitabilityAccess) {
            return null;
          }

          if (item.requireHoldedAccounting && !isHoldedAccounting()) {
            return null;
          }

          // Sección del menú: ocultar si no quedan hijos visibles
          if (item.isMenuSection) {
            if (!filteredChildren || filteredChildren.length === 0) {
              return null;
            }
            return {
              ...item,
              children: filteredChildren,
            };
          }

          // Si tiene children filtrados, incluir solo los que pasaron el filtro
          if (item.children) {
            return {
              ...item,
              children: filteredChildren && filteredChildren.length > 0 ? filteredChildren : undefined,
            };
          }

          return item;
        })
        .filter((item): item is MenuItem => item !== null);
    },
    [
      canAccess,
      hasAllPermissions,
      hasAnyPermission,
      isAdminRole,
      isWalletVisible,
      isHoldedAccounting,
      crmEnabled,
      profitabilityAccess,
    ],
  );

  /**
   * Filtra items del dropdown recursivamente
   * - Verifica teamTypes si están especificados (OBLIGATORIO)
   * - Verifica requireRoleAdmin si está definido (OBLIGATORIO si definido)
   * - Verifica permissions si están especificados (OPCIONAL)
   */
  const filterDropdownItems = useCallback(
    (items: DropdownItem[]): DropdownItem[] => {
      return items
        .map((item) => {
          // 🎯 Si es un separator o label, no filtrar
          if (item.type === 'separator' || item.type === 'label') {
            return item;
          }

          // Si el item tiene children, filtrarlos recursivamente
          let filteredChildren: DropdownItem[] | undefined;
          if (item.children && item.children.length > 0) {
            filteredChildren = filterDropdownItems(item.children);
          }

          // 1. Verificar team_type (OBLIGATORIO si está definido)
          if (item.teamTypes && item.teamTypes.length > 0 && !canAccess(item.teamTypes, [])) {
            return null;
          }

          // 2. Verificar requireRoleAdmin (OBLIGATORIO si está definido)
          if (item.requireRoleAdmin && !isAdminRole()) {
            return null;
          }

          // 3. Verificar permisos (OPCIONAL)
          let hasPermission = true;
          if (item.permissions && item.permissions.length > 0) {
            if (item.requireAll) {
              hasPermission = hasAllPermissions(item.permissions);
            } else {
              hasPermission = hasAnyPermission(item.permissions);
            }
          }

          if (!hasPermission) {
            return null;
          }

          // 4. Verificar requireWalletVisible (OBLIGATORIO si está definido)
          if (item.requireWalletVisible && !isWalletVisible()) {
            return null;
          }

          // 5. Verificar requireCRMEnabled (OBLIGATORIO si está definido)
          if (item.requireCRMEnabled && !crmEnabled) {
            return null;
          }

          if (item.requireProfitabilityAccess && !profitabilityAccess) {
            return null;
          }

          if (item.requireHoldedAccounting && !isHoldedAccounting()) {
            return null;
          }

          // Si tiene children filtrados, incluir solo los que pasaron el filtro
          if (item.children) {
            return {
              ...item,
              children: filteredChildren && filteredChildren.length > 0 ? filteredChildren : undefined,
            };
          }

          return item;
        })
        .filter((item): item is DropdownItem => item !== null);
    },
    [
      canAccess,
      hasAllPermissions,
      hasAnyPermission,
      isAdminRole,
      isWalletVisible,
      isHoldedAccounting,
      crmEnabled,
      profitabilityAccess,
    ],
  );

  return { filterMenuItems, filterDropdownItems };
}
